import { Box, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useIsMobile, useL10n } from '@uplift/hooks';
import React, { useState } from 'react';
import { InsuranceSectionItem } from './insurance-section-item';
import { AddInsuranceSection, InsuranceSection, InsuranceSettingsType } from '../Settings/types';
import { Button } from '../../../Button';
import { EditableSectionContainer, EditableSectionRow, useEditableSectionsContext } from '../../../EditableSection';
import { DeleteInsuranceModal } from './delete-insurance-modal.component';
import { InsuranceFixedEditAction } from './insurance-fixed-edit-action.component';
import { AddInsurance } from '../forms/add-insurance-section-item';
import { useInsurances } from '../hooks/use-insurance.hook';
import { InsuranceEmpty } from './insurance-empty.component';


export const InsuranceSettings: React.VFC<InsuranceSettingsType> = ({ userId }) => {
  const l10n = useL10n();
  const [ deleteInsurance, setDeleteInsurance ] = useState<InsuranceSection | null>(null);
  const isMobile = useIsMobile('sm');
  const { setEditKey } = useEditableSectionsContext();
  const { data: insurancesData } = useInsurances(userId);

  const primaryInsuranceData = insurancesData?.[InsuranceSection.primaryInsurance];
  const secondaryInsuranceData = insurancesData?.[InsuranceSection.secondaryInsurance];

  const onAddInsurance = (addInsuranceKey: AddInsuranceSection) => setEditKey(addInsuranceKey);

  if (!primaryInsuranceData && !secondaryInsuranceData) {
    return (
      <AddInsurance patientId={userId} addInsuranceSection={AddInsuranceSection.addPrimaryInsurance}>
        <InsuranceEmpty userId={userId} onAddInsurance={() => onAddInsurance(AddInsuranceSection.addPrimaryInsurance)} />
      </AddInsurance>
    );
  }

  let secondary: React.ReactNode = null;

  if (secondaryInsuranceData) {
    secondary = (
      <InsuranceSectionItem
        patientId={userId}
        insuranceSection={InsuranceSection.secondaryInsurance}
        title={l10n.accountSettings.insurance.secondaryInsurance}
        onDeleteInsurance={setDeleteInsurance}
      />
    );
  } else {
    const addInsuranceButton = (
      <Button
        color="secondary"
        onClick={() => onAddInsurance(AddInsuranceSection.addSecondaryInsurance)}
        startIcon={isMobile ? <Add /> : undefined}
      >
        {l10n.accountSettings.addInsurance.addInsurance}
      </Button>
    );

    secondary = (
      <EditableSectionContainer title={l10n.accountSettings.addInsurance.haveAnotherInsurance}>
        <EditableSectionRow
          kind="custom"
          actions={isMobile ? null : addInsuranceButton}
          rowKey="addInsurance"
        >
          <Box display="flex" flexDirection="column" gap="1rem">
            <Typography>{l10n.accountSettings.addInsurance.onlyInsurancesAcceptedByCareTeam}</Typography>
            {isMobile ? addInsuranceButton : null}
          </Box>
        </EditableSectionRow>
      </EditableSectionContainer>
    );
  }


  return (
    <Box display="flex" flexDirection="column" gap="2.5rem">
      <AddInsurance patientId={userId} addInsuranceSection={[ AddInsuranceSection.addSecondaryInsurance, AddInsuranceSection.replacePrimaryInsurance ]}>
        {deleteInsurance &&  (
          <DeleteInsuranceModal
            deleteInsuranceSection={deleteInsurance}
            onClose={() => setDeleteInsurance(null)}
            onAddNewInsurance={() => onAddInsurance(AddInsuranceSection.replacePrimaryInsurance)}
            userId={userId}
          />
        )}

        <InsuranceSectionItem
          patientId={userId}
          insuranceSection={InsuranceSection.primaryInsurance}
          title={secondaryInsuranceData ? l10n.accountSettings.insurance.primaryInsurance : l10n.accountSettings.insurance.yourInsurance}
          onDeleteInsurance={setDeleteInsurance}
        />
        {secondary}

        <InsuranceFixedEditAction
          primaryAction={{
            onClick: () => setEditKey(InsuranceSection.primaryInsurance),
            text: secondaryInsuranceData ? l10n.accountSettings.insurance.editPrimary : l10n.accountSettings.insurance.editInsurance,
          }}
          secondaryAction={{
            onClick: () => setEditKey(InsuranceSection.secondaryInsurance),
            hidden: !secondaryInsuranceData
          }}
        />
      </AddInsurance>
    </Box>
  );
};
